import { DOMProps } from '@react-types/shared';
import { filterDOMProps } from '@react-aria/utils';
import React, { ElementType, forwardRef } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/text/text.module.css';
import {
  SapphireStyleProps,
  TextStyleProps,
  useTextStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export interface TypographySubheadingProps
  extends DOMProps,
    TextStyleProps,
    SapphireStyleProps,
    GlobalDomAttributes {
  children: React.ReactNode;
  /**
   * The actual DOM element to render.
   * @default 'div'
   */
  elementType?: string;
  /**
   * Sets level of the hierarchy of the subheading.
   * @default 'md'
   */
  size?: 'md' | 'sm';
  /**
   * The semantic color of the text
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'positive'
    | 'negative'
    | 'warning'
    | 'informative';
  /**
   * If text is bold
   * @default false
   */
  isBold?: boolean;
}

/**
 * A subheading is a piece of text used under regular headings (h1-h6).
 * A subheading is not itself a heading, but it can assist a heading by
 * providing a description to the heading it accompanies.
 * By default, it renders as block text (div), but you can render it as
 * anything else.
 *
 * See:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#text_content
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#inline_text_semantics
 */
export const Subheading = forwardRef(function Subheading(
  props: TypographySubheadingProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  useThemeCheck();

  const {
    children,
    size = 'md',
    color = 'primary',
    elementType: CustomElement = 'div' as ElementType,
    isBold = false,
    ...otherProps
  } = props;
  const { styleProps } = useTextStyleProps(otherProps);
  const sizeClassName = styles[`sapphire-text--subheading-${size}` as const];
  const colorClassName =
    color === 'primary'
      ? undefined
      : styles[`sapphire-text--${color}` as const];

  return (
    <CustomElement
      {...filterDOMProps(props, { global: true })}
      {...styleProps}
      className={clsx(
        styleProps.className,
        styles['sapphire-text'],
        sizeClassName,
        colorClassName,
        {
          [styles['sapphire-text--strong']]: isBold,
        }
      )}
      style={styleProps.style}
      ref={ref}
    >
      {children}
    </CustomElement>
  );
});
